<script setup>
import SiteLogo from 'components/SiteLogo.vue';
import TextLink from 'components/TextLink.vue';
import AppRoute from 'src/router/app-route.js';
import { Route } from 'src/router/routes.js';

const companyLinks = [
    {
        label: 'About Us',
        route: AppRoute.build(Route.AboutUsPage)
    },
    {
        label: 'Contact Us',
        route: AppRoute.build(Route.ContactPage)
    }
];

const legalLinks = [
    {
        label: 'Privacy Policy',
        route: AppRoute.build(Route.PrivacyPolicyPage)
    },
    {
        label: 'Terms of Service',
        route: AppRoute.build(Route.TermsOfServicePage)
    }
];

const products = [
    {
        name: 'DoubleTime',
        description: 'Time tracking & invoicing for professionals',
        url: 'https://doubletime.io'
    }
];

const socialLinks = [
    {
        platform: 'Facebook',
        icon: 'img:/icons/brand-logo_facebook.svg',
        url: 'https://www.facebook.com/xprtjobs'
    },
    {
        platform: 'LinkedIn',
        icon: 'img:/icons/brand-logo_linkedin.svg',
        url: 'https://www.linkedin.com/showcase/xprtjobs'
    },
    {
        platform: 'X (Twitter)',
        icon: 'img:/icons/brand-logo_x.svg',
        url: 'https://x.com/XprtJobs'
    }
];
</script>

<template>
    <footer class="q-py-lg">
        <q-separator class="gradient-separator q-mb-lg"/>
        <div class="container-md q-px-md q-pt-lg">
            <div class="footer-sections flex column row-md wrap text-center text-md-left q-col-gutter-x-xl q-col-gutter-y-sm">
                <!-- Logo and brief description -->
                <div class="footer-section footer-section__logo flex column items-center items-md-start full-width">
                    <div class="logo-container q-mb-md">
                        <SiteLogo height="29px" lazy-load width="177px"/>
                    </div>
                    <p class="tagline text-body2 q-mb-md">Helping job seekers find their dream careers with expert advice and tools.</p>
                    <div class="social-links flex justify-center q-mb-lg q-mb-md-sm">
                        <a
                            v-for="social in socialLinks"
                            :key="social.platform"
                            :aria-label="social.platform"
                            :href="social.url"
                            class="social-link q-card q-card--bordered shadow-0 q-mr-sm"
                            rel="noopener"
                            target="_blank"
                        >
                            <q-icon :name="social.icon" size="sm"/>
                        </a>
                    </div>
                </div>

                <div class="secondary-sections flex no-wrap wrap-md justify-between justify-md-start self-center self-md-start q-col-gutter-x-xl q-col-gutter-y-sm">
                    <!-- Products -->
                    <div class="footer-section footer-section__products">
                        <h6 class="q-mt-none q-mb-md">Products</h6>
                        <div class="column q-gutter-y-sm">
                            <div v-for="product in products" :key="product.name" class="q-ml-n-sm q-mt-xs product-item rounded-borders">
                                <a :href="product.url" class="product-link row justify-between items-center q-pa-sm rounded-borders" rel="noopener" target="_blank">
                                    <div class="column">
                                        <div class="text-weight-medium">{{ product.name }}</div>
                                        <div class="text-caption text-grey-7">{{ product.description }}
                                            <q-icon class="q-ml-xs product-icon" name="sym_r_open_in_new" size="xs"/>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Company Links -->
                    <div class="footer-section footer-section__company">
                        <h6 class="q-mt-none q-mb-md">Company</h6>
                        <ul class="q-ma-none q-pa-none">
                            <li v-for="link of companyLinks" :key="link.label" class="q-mb-xs">
                                <TextLink :label="link.label" :route="link.route"/>
                            </li>
                        </ul>
                    </div>

                    <!-- Legal Links -->
                    <div class="footer-section footer-section__legal">
                        <h6 class="q-mt-none q-mb-md">Legal</h6>
                        <ul class="q-ma-none q-pa-none">
                            <li v-for="link of legalLinks" :key="link.label" class="q-mb-xs">
                                <TextLink :label="link.label" :route="link.route"/>
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Blog section commented out until ready
                <div class="footer-section footer-section__blog">
                    <h6 class="text-weight-bold q-mt-none q-mb-md">Job Seeker Blog</h6>
                    <p class="text-body2">Expert advice, interview tips, and career guidance to help you land your dream job.</p>
                    <q-btn unelevated color="primary" label="Read Latest Posts" href="#" class="q-mt-sm" />
                </div>
                -->
            </div>
        </div>

        <q-separator class="gradient-separator q-mt-xl full-width"/>

        <div class="container-md q-px-md">
            <!-- Copyright -->
            <div class="copyright-row column row-md text-center text-md-start justify-between items-center q-mt-md">
                <div class="q-mb-sm q-mb-md-none">&copy; {{ new Date().getFullYear() }}, All Rights Reserved.</div>
                <div class="text-caption">Designed with
                    <q-icon class="material-symbols-rounded-fill" color="secondary" name="sym_r_favorite" size="xs"/>
                    for professionals
                </div>
            </div>
        </div>
    </footer>
</template>

<style lang="scss" scoped>
footer {
    background-color: white;

    .tagline {
        max-width: 350px;
    }

    ul li {
        list-style: none;
    }

    .social-link {
        align-items: center;
        background-color: rgba(250, 250, 250, 0.4);
        border-radius: 50%;
        display: flex;
        flex-direction: row;
        height: 44px;
        justify-content: center;
        transition: all 0.2s ease;
        width: 44px;

        &:hover {
            background: rgba($secondary, 0.06) !important;
            border-color: rgba($secondary, 0.09) !important;
            text-decoration: none;
            transition: all 120ms ease-in-out, background-color 120ms, color 0ms;
        }
    }

    .footer-section__logo {
        flex: 2;
        min-width: 250px;
    }

    .secondary-sections {
        flex: 3;

        .footer-section {
            flex: 1;
            min-width: 140px;
        }
    }

    .product-item {
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba($primary, 0.05);
        }
    }

    .product-link {
        color: $default;

        &:hover {
            color: $primary;
            text-decoration: none;

            .product-icon {
                opacity: 1;
                transform: translateX(0);
            }
        }
    }

    .product-icon {
        color: $primary;
        opacity: 0;
        transform: translateX(-4px);
        transition: all 0.2s ease;
    }
}
</style>
